import React, { useRef, useEffect, useMemo, forwardRef } from "react";
import { useJackpotPlates } from "../../../Games/Shared/jackpotPlates";
import { Container } from "@pixi/react";
import { useSelector } from "react-redux";
import {
  selectHoldAndWinActive,
  selectHoldAndWinKinds,
  selectHoldAndWinValues,
  selectHoldAndWinPoints,
} from "../../../State/Slices/holdAndWinSlice";
import { selectJackpotMultipliers } from "../../../State/Slices/gameSettingsSlice";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../pixiSettings";
import {
  useSlotRegistry,
  useSlot,
  getCounterScale,
} from "../../../Contexts/SlotRegistryContext";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import HoldAndWinGameViewController, {
  HOLD_AND_WIN_GAME_VIEW_SLOTS,
  HOLD_AND_WIN_CELL,
  holdAndWinSceneScale,
} from "./HoldAndWinGameViewController";
import HoldAndWinBoard from "../../../Games/HoldAndWin/HoldAndWinBoard";
import HoldAndWinOverlay from "../../../Games/HoldAndWin/HoldAndWinOverlay";
import HoldAndWinCounter from "../HoldAndWinCounter/HoldAndWinCounter";

/**
 * HoldAndWinGameView — the Cleopatra's Fire Hold & Win room.
 *
 * The scene only, for now: the hold_and_win_game_view spine (layout skin +
 * idle) with its two reel slots published and their containers injected. The
 * 5x8 board goes into them when it is built (Zeus Pot Collection's per-cell
 * spin — see the H&W decisions memory).
 *
 * Which room is on screen will be the H&W active flag once it exists; until
 * then FEATURES_CONFIG.DEV_TOOLS.PREVIEW_HOLD_AND_WIN_VIEW shows it over the
 * base room so the layout can be checked. Hidden by ALPHA, never `visible` —
 * see GameViewSimple.
 */
const HoldAndWinGameView = forwardRef((props, ref) => {
  const controllerRef = useRef(null);
  // The jackpot plates' values, on the current bet (Games/Shared/jackpotPlates).
  useJackpotPlates(controllerRef);
  const containerRef = useRef(null);
  const reelContainerRef = useRef(null);
  const overlayContainerRef = useRef(null);
  // The spines over the board — the board hands landed cells to it.
  const boardOverlayRef = useRef(null);

  const {
    dimensions,
    symbolWidth,
    symbolHeight,
    symbolScale,
    device,
    orientation,
    layout,
    isMobileSmall,
  } = useDimensions();

  const slotRegistry = useSlotRegistry();
  const { holdAndWinGameViewControllerRef, holdAndWinBoardRef } = useReelRefs();

  // The room is on screen while the feature is (holdAndWin.active, raised at
  // the wipe's midpoint), or for the dev preview.
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const kinds = useSelector(selectHoldAndWinKinds);
  const values = useSelector(selectHoldAndWinValues);
  const points = useSelector(selectHoldAndWinPoints);
  const jackpots = useSelector(selectJackpotMultipliers);
  const devPreview =
    !!FEATURES_CONFIG.DEV_TOOLS?.PREVIEW_HOLD_AND_WIN_VIEW && !holdAndWinActive;
  const visible = holdAndWinActive || devPreview;

  // This room is drawn at ITS OWN scale (HOLD_AND_WIN_LAYOUT_CONFIGS), not
  // the base board's symbolScale — so the injected board is counter-scaled
  // by the same number the controller draws the spine at.
  const sceneScale = useMemo(
    () => holdAndWinSceneScale(dimensions, layout),
    [dimensions, layout],
  );
  const spineCounterScale = useMemo(
    () => getCounterScale(sceneScale),
    [sceneScale],
  );

  // One board cell in the injected container's pixels: the frame's 170 x 139
  // spine-unit cell at this room's scale. The container is counter-scaled by
  // the same number, and the `reels` bone's own layout scale (0.8485 in
  // landscape) applies to board and frame alike, so they stay in register.
  const cellWidth = HOLD_AND_WIN_CELL.WIDTH * sceneScale;
  const cellHeight = HOLD_AND_WIN_CELL.HEIGHT * sceneScale;

  // ─── Controller ───
  useEffect(() => {
    if (!containerRef.current || controllerRef.current) return;

    const realDevice = isMobileSmall ? PixiSettings.DEVICE.MOBILE_SMALL : device;

    controllerRef.current = new HoldAndWinGameViewController(
      containerRef.current,
      dimensions,
      symbolWidth,
      symbolHeight,
      symbolScale,
      realDevice,
      orientation,
      layout,
      slotRegistry,
    );

    if (holdAndWinGameViewControllerRef) {
      holdAndWinGameViewControllerRef.current = controllerRef.current;
    }

    return () => {
      if (controllerRef.current) {
        controllerRef.current.destroy();
        controllerRef.current = null;
        if (holdAndWinGameViewControllerRef) {
          holdAndWinGameViewControllerRef.current = null;
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Inject the reel container into the spine's reel slot ───
  const reelSlot = useSlot(HOLD_AND_WIN_GAME_VIEW_SLOTS.REEL_SET);
  useEffect(() => {
    if (!reelSlot || !reelContainerRef.current) return;
    const node = reelContainerRef.current;
    reelSlot.addChild(node);
    return () => {
      if (node.parent === reelSlot) reelSlot.removeChild(node);
    };
  }, [reelSlot]);

  // ─── Inject the overlay container into the spine's overlay slot ───
  const overlaySlot = useSlot(HOLD_AND_WIN_GAME_VIEW_SLOTS.OVERLAY_REEL_SET);
  useEffect(() => {
    if (!overlaySlot || !overlayContainerRef.current) return;
    const node = overlayContainerRef.current;
    overlaySlot.addChild(node);
    return () => {
      if (node.parent === overlaySlot) overlaySlot.removeChild(node);
    };
  }, [overlaySlot]);

  // ─── Undo the spine's inherited scale on both injected containers ───
  useEffect(() => {
    if (reelContainerRef.current) {
      reelContainerRef.current.scale.set(spineCounterScale);
    }
    if (overlayContainerRef.current) {
      overlayContainerRef.current.scale.set(spineCounterScale);
    }
  }, [spineCounterScale]);

  // ─── Resize ───
  useEffect(() => {
    if (!controllerRef.current) return;
    const realDevice = isMobileSmall ? PixiSettings.DEVICE.MOBILE_SMALL : device;
    controllerRef.current.resize(
      dimensions,
      symbolWidth,
      symbolHeight,
      symbolScale,
      realDevice,
      orientation,
      layout,
    );
  }, [
    dimensions,
    symbolWidth,
    symbolHeight,
    symbolScale,
    device,
    orientation,
    layout,
    isMobileSmall,
  ]);

  return (
    <Container
      ref={containerRef}
      name="hold_and_win_game_view"
      sortableChildren
      // The same layer as the other two rooms — they never share the screen.
      zIndex={0}
      alpha={visible ? 1 : 0.001}
    >
      {/* Board — injected into the spine's reel slot, clipped to the window */}
      <Container
        ref={reelContainerRef}
        name="hold_and_win_reel_container"
        sortableChildren
        eventMode="passive"
      >
        {/* The 5 x 8 board. Only while the room is shown; for now that is
            the dev preview, which also runs the board's spin loop. */}
        {visible && (
          <HoldAndWinBoard
            cellWidth={cellWidth}
            cellHeight={cellHeight}
            overlayRef={boardOverlayRef}
            devSpinLoop={devPreview}
            kinds={kinds}
            values={values}
            points={points}
            jackpots={jackpots}
            controlRef={holdAndWinBoardRef}
          />
        )}
      </Container>

      {/* Overlay — injected into the spine's overlay slot, above the frame */}
      <Container
        ref={overlayContainerRef}
        name="hold_and_win_overlay_reel_container"
        zIndex={2}
        sortableChildren
        eventMode="passive"
      >
        {/* The landed cells' spines, laid out like the board. Above the
            frame and outside the window clip. */}
        {visible && (
          <HoldAndWinOverlay
            ref={boardOverlayRef}
            cellWidth={cellWidth}
            cellHeight={cellHeight}
          />
        )}
      </Container>

      {/* The respin counter — moves itself into the room's
          respin_counter_placeholder slot (under the bottom row). */}
      <HoldAndWinCounter />
    </Container>
  );
});

HoldAndWinGameView.displayName = "HoldAndWinGameView";

export default HoldAndWinGameView;
